import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { getDecisionsTypePickerSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/EntityPickerService';
import { EpickTree, IEpickNode } from 'components/EntityPicker/EpickTree';
import { entityPickerFetcher } from 'components/EntityPicker/entityPickerFetcher';
import { IEntityPickerTreeModel } from 'components/EntityPicker/defaultEpickTreeModel';
import { inputDesignerEpickTreeModel } from 'components/EntityPicker/inputDesignerEpickTreeModel';
import { DndInpuData } from './DndInputData';
import './Toolbox.less';
import { useDragAndDrop, createDndEvent } from 'hooks/useDragAndDrop';
import { Overlay } from 'components/layer/Overlay';
import { getMaxZIndex } from 'utils/DomUtils';
import { useDidUpdateEffect } from 'hooks/useDidUpdateEffect';
import { LinkButton } from 'components/buttons/LinkButton';
import { IPoint } from 'utils/GeometryUtils';
import { SearchField } from 'components/inputs/SearchField';
import { globalSearchEntityPickerTreeModel } from 'components/EntityPicker/globalSearchEntityPickerTreeModel';
import { DecisionsTypePickerSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/DecisionsTypePickerSettings';
import { generateGuid } from 'utils/Guid';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { registerForEventAndSubscribe, unRegisterForEventAndUnsubscribe } from 'utils/ClientEventUtils';

export interface ToolboxProps {
  onCreateTypeClick?: () => void;
  onTypeDropped: (fullTypeName: string, point: IPoint) => void;
}

export function Toolbox({ onCreateTypeClick, onTypeDropped }: ToolboxProps) {
  // toolBoxId is used to re-render tree completely otherwise loader will be shown under tree when we start to search
  const [toolBoxId, setToolBoxId] = useState(generateGuid());
  const settings = useRef<DecisionsTypePickerSettings>();
  const [epickTreeModel, setEpickModel] = useState<IEntityPickerTreeModel | null>(null);
  const [dndNode, setDndNode] = useState<IEpickNode | null | undefined>(null);
  const { start, endPoint, isActive } = useDragAndDrop();
  const [searchText, setSearchText] = useState('');
  const zIndex = useMemo(() => getMaxZIndex(), []);

  const updateModel = useCallback(async () => {
    setToolBoxId(generateGuid());

    if (settings.current == null) {
      settings.current = await getDecisionsTypePickerSettings({});
    }
    const dataloader = entityPickerFetcher({
      filterNames: settings.current.FilterNames,
      types: settings.current.Types,
    });
    if (searchText) {
      setEpickModel(globalSearchEntityPickerTreeModel(searchText, settings.current, dataloader));
    } else {
      setEpickModel(inputDesignerEpickTreeModel(settings.current, dataloader));
    }
  }, [searchText]);

  useEffect(() => {
    // When new DataType has been created need to refresh Toolbox
    registerForEventAndSubscribe('DataTypeChanged', toolBoxId, updateModel);

    return () => {
      unRegisterForEventAndUnsubscribe('DataTypeChanged', toolBoxId, updateModel);
    };
  }, [toolBoxId, updateModel]);

  const handleNodeMouseDown = useCallback(
    (node: ITreeNode<IEpickNode>, e: React.MouseEvent) => {
      if (!node.data?.expandable) {
        start(createDndEvent(e));
        setDndNode(node.data);
      }
    },
    [start]
  );

  const clearSearch = useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  useEffect(() => {
    updateModel();
  }, [updateModel]);

  useDidUpdateEffect(() => {
    if (!isActive && dndNode) {
      onTypeDropped(dndNode.id, endPoint);
      setDndNode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dndNode, isActive, onTypeDropped]);

  return (
    <div className="dpc-input-designer-toolbox">
      <LinkButton className="dpc-input-designer-toolbox__new-type" color="primary" onClick={onCreateTypeClick}>
        Create New Data Type
      </LinkButton>

      <SearchField
        className="dpc-input-designer-toolbox__search"
        value={searchText}
        onSearch={setSearchText}
        onClear={clearSearch}
      />
      <div className="dpc-input-designer-toolbox__tree-wrapper">
        {epickTreeModel != null && (
          <EpickTree key={toolBoxId} epickTreeModel={epickTreeModel} onNodeMouseDown={handleNodeMouseDown} />
        )}
      </div>
      {isActive && dndNode && (
        <Overlay id="dnd-overlay" className="ui-front" zIndex={zIndex}>
          <DndInpuData style={{ left: endPoint.x, top: endPoint.y }}>{dndNode.name}</DndInpuData>
        </Overlay>
      )}
    </div>
  );
}
